import React from 'react';
import {
    ColumnHeaderDetails,
    ColumnHeaderTitle,
    ColumnHeaderWrapper,
    ColumnSubHeader,
    ColumnSubHeaderTitle,
    PrimaryHeaderWrapper,
} from './BunkerRob.styles';
import { FuelRobColumn } from 'src/types/ViewModel/Bunkering/FuelRobColumn';
import { AolGroup } from 'src/types/ViewModel/AolItem';
import { getAolTabInfo, getEndTimeText } from 'src/components/aol/AolUtils';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { useBunkerContext } from 'src/context/BunkerContext';
import { ListButton } from 'src/styles/button.style';
import { useAuthStore } from 'src/context/AuthContext';

interface ColumnHeaderProps {
    robColumn: FuelRobColumn;
    aolGroup: AolGroup;
    openbunkerLossModal: (column: FuelRobColumn) => void;
    displayLossColumn: boolean;
}

const BunkerRobColumnHeader: React.FC<ColumnHeaderProps> = ({ robColumn, aolGroup, openbunkerLossModal, displayLossColumn }) => {
    const { bunkerings, bunkeringEvents } = useBunkerContext();
    const { role } = useAuthStore();

    const renderHeader = (title: string, details: string, subHeaderTitles: string[], noonReportId?: number) => {
        return (
            <>
                <PrimaryHeaderWrapper
                    noonReportType={
                        aolGroup.aolDataItems.find((aolDataItem) => aolDataItem?.noonReport?.metadata?.noonReportId === robColumn.noonReportId)
                            ?.noonReport?.metadata?.noonReportType
                    }
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <ColumnHeaderTitle>{title}</ColumnHeaderTitle>
                        {noonReportId && (
                            <ListButton onClick={() => openbunkerLossModal(robColumn)} disabled={!role.isWriter}>
                                Loss
                            </ListButton>
                        )}
                    </div>

                    <ColumnHeaderDetails>{details}</ColumnHeaderDetails>
                </PrimaryHeaderWrapper>
                <ColumnSubHeader>
                    {subHeaderTitles.map((subHeaderTitle, index) => (
                        <ColumnSubHeaderTitle key={index}>{subHeaderTitle}</ColumnSubHeaderTitle>
                    ))}
                </ColumnSubHeader>
            </>
        );
    };

    const renderAolHeader = () => {
        const aolItem = aolGroup.aolDataItems.find((aolDataItem) => aolDataItem?.noonReport?.metadata?.noonReportId === robColumn.noonReportId);
        const title = getAolTabInfo(aolItem);
        const details = getEndTimeText(aolItem);
        let subHeaderTitles = ['Consumed', 'Calculated ROB', 'ROB', 'Verified by sounding', 'Transfer'];
        if (displayLossColumn) {
            subHeaderTitles = ['Consumed', 'Loss', 'Calculated ROB', 'ROB', 'Verified by sounding', 'Transfer'];
        }
        return renderHeader(title, details, subHeaderTitles, aolItem?.noonReport?.metadata?.noonReportId);
    };

    const renderDebunkeringHeader = () => {
        const bunkering = bunkerings.find((b) => b.bunkeringGuid === robColumn.bunkeringGuid);
        const debunkeringEvent = bunkeringEvents.find((b) => b.eventID === robColumn.debunkeringEventId);
        const title = `Debunkering ${prettifyDateTimeHHmm(debunkeringEvent?.bunkeringDateTimeLocal)}`;
        const details = bunkering?.bunkeringReference;
        const subHeaderTitles = ['Debunkered', 'Calculated ROB'];
        return renderHeader(title, details, subHeaderTitles);
    };

    const renderBunkeringHeader = () => {
        const bunkering = bunkerings.find((b) => b.bunkeringGuid === robColumn.bunkeringGuid);
        const title = `Bunkering: ${bunkering?.bunkeringReference}`;
        const details = prettifyDateTimeHHmm(bunkering?.bunkeringDateTime);
        const subHeaderTitles = ['Received', 'Calculated ROB', 'ROB', 'Verified by sounding'];
        return renderHeader(title, details, subHeaderTitles);
    };

    if (!aolGroup) return null;

    return (
        <ColumnHeaderWrapper>
            {robColumn?.noonReportId || robColumn?.noonReportId === 0 ? renderAolHeader() : null}
            {robColumn?.debunkeringEventId ? renderDebunkeringHeader() : null}
            {!robColumn?.noonReportId && !robColumn?.debunkeringEventId ? renderBunkeringHeader() : null}
        </ColumnHeaderWrapper>
    );
};

export default BunkerRobColumnHeader;
